import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Home, ClipboardList, CheckCircle2, XCircle, Copy, ExternalLink, Pencil, Trash2 } from "lucide-react";

const EMPTY_FORM = {
  unit: "", monthly_rent: 2500, bedrooms: 2, bathrooms: 2, sqft: "",
  description: "", amenities: "", available_date: new Date().toISOString().slice(0, 10),
  status: "available",
};

const LISTING_STATUSES = ["draft", "available", "application_pending", "leased", "withdrawn"];

export default function LeasingPipelinePage() {
  const { propertyId } = useProperty();
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [appStats, setAppStats] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // listing object when editing
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    if (!propertyId) return;
    try {
      const { data: l } = await api.get(`/admin/leasing/listings?property_id=${propertyId}`);
      setListings(l.items || []);
      const { data: a } = await api.get(`/admin/leasing/applications?property_id=${propertyId}`);
      setApplications(a.items || []);
      setAppStats(a.stats || null);
    } catch (e) { toast.error("Failed to load"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [propertyId]);
  const openNewListing = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEditListing = (l) => {
    setEditing(l);
    setForm({
      unit: l.unit || "",
      monthly_rent: l.monthly_rent || 0,
      bedrooms: l.bedrooms ?? 1,
      bathrooms: l.bathrooms ?? 1,
      sqft: l.sqft || "",
      description: l.description || "",
      amenities: (l.amenities || []).join(", "),
      available_date: l.available_date || new Date().toISOString().slice(0, 10),
      status: l.status || "available",
    });
    setDialogOpen(true);
  };
  const saveListing = async () => {
    if (!form.unit.trim()) return toast.error("Unit is required");
    const body = {
      unit: form.unit.trim(),
      monthly_rent: parseFloat(form.monthly_rent),
      bedrooms: parseInt(form.bedrooms),
      bathrooms: parseFloat(form.bathrooms),
      sqft: form.sqft ? parseInt(form.sqft) : null,
      description: form.description || null,
      amenities: form.amenities ? form.amenities.split(",").map((s) => s.trim()).filter(Boolean) : [],
      available_date: form.available_date,
      status: form.status,
    };
    try {
      if (editing) {
        // PATCH doesn't take property_id
        await api.patch(`/admin/leasing/listings/${editing.id}`, body);
        toast.success("Listing updated");
      } else {
        await api.post("/admin/leasing/listings", { property_id: propertyId, ...body, photos: [] });
        toast.success("Listing published");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteListing = async (l) => {
    if (!window.confirm(`Delete listing for unit ${l.unit}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/leasing/listings/${l.id}`);
      toast.success("Listing deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openApp = async (a) => {
    try {
      const { data } = await api.get(`/admin/leasing/applications/${a.id}`);
      setDetail(data);
    } catch (e) { toast.error("Failed"); }
  };

  const runScreening = async () => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/admin/leasing/applications/${detail.id}/run-screening`);
      toast.success(`Screening: ${data.screening_report.recommend}`);
      openApp(detail);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const decide = async (decision) => {
    if (!detail) return;
    const notes = window.prompt(`${decision === "approve" ? "Approval" : "Denial"} notes (optional):`);
    if (notes === null) return;
    try {
      const { data } = await api.post(`/admin/leasing/applications/${detail.id}/${decision}`, { notes, create_resident: decision === "approve" });
      if (decision === "approve") {
        toast.success(`Approved. Created resident ${data.resident_id?.slice(0, 8)} + draft lease ${data.lease_id?.slice(0, 8)}`);
      } else {
        toast.success("Application denied");
      }
      setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const publicUrl = (lid) => `${window.location.origin}/listings/${lid}`;

  const status_pill = (s) => {
    const colors = { available: "default", application_pending: "secondary", leased: "outline", draft: "outline", withdrawn: "destructive", submitted: "secondary", screening: "secondary", approved: "default", denied: "destructive" };
    return <Badge variant={colors[s] || "outline"}>{s}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="leasing-pipeline-page">
      <PageHeader
        eyebrow="Leasing"
        title="Vacancy & Applications"
        subtitle="Publish available units, share public links with prospects, review applications, run screening, approve → auto-creates resident + draft lease."
        actions={
          <Button onClick={openNewListing} className="gap-2" data-testid="new-listing-btn">
            <Plus size={14} /> New listing
          </Button>
        }
      />

      <div className="flex gap-2 border-b border-white/10">
        <button
          onClick={() => setTab("listings")}
          className={`px-4 py-2 text-sm ${tab === "listings" ? "border-b-2 border-primary" : "text-muted-foreground"}`}
          data-testid="tab-listings"
        >
          <Home size={12} className="inline mr-1" /> Listings ({listings.length})
        </button>
        <button
          onClick={() => setTab("applications")}
          className={`px-4 py-2 text-sm ${tab === "applications" ? "border-b-2 border-primary" : "text-muted-foreground"}`}
          data-testid="tab-applications"
        >
          <ClipboardList size={12} className="inline mr-1" /> Applications ({applications.length})
          {appStats?.submitted > 0 && <Badge variant="destructive" className="ml-2 text-[10px]">{appStats.submitted} new</Badge>}
        </button>
      </div>

      {tab === "listings" && (
        <Card className="border-white/10 bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead>Unit</TableHead>
                <TableHead>Bed/Bath</TableHead>
                <TableHead className="text-right">Rent</TableHead>
                <TableHead>Available</TableHead>
                <TableHead>Applications</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listings.length === 0 && (
                <TableRow className="border-white/5"><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm" data-testid="listings-empty">
                  No listings yet. Publish a unit so prospects can apply.
                </TableCell></TableRow>
              )}
              {listings.map((l) => (
                <TableRow key={l.id} className="border-white/5" data-testid={`listing-row-${l.id}`}>
                  <TableCell className="font-medium">{l.unit}</TableCell>
                  <TableCell className="text-sm">{l.bedrooms}bd / {l.bathrooms}ba{l.sqft && ` · ${l.sqft} sqft`}</TableCell>
                  <TableCell className="text-right font-mono">${l.monthly_rent.toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{l.available_date}</TableCell>
                  <TableCell>{l.applications_count || 0}</TableCell>
                  <TableCell>{status_pill(l.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-0.5 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(publicUrl(l.id)); toast.success("Public link copied"); }}
                        data-testid={`copy-link-${l.id}`} title="Copy public link">
                        <Copy size={12} />
                      </Button>
                      <a href={publicUrl(l.id)} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="ghost" title="Open public listing"><ExternalLink size={12} /></Button>
                      </a>
                      <Button size="sm" variant="ghost" onClick={() => openEditListing(l)} data-testid={`edit-listing-${l.id}`} title="Edit listing">
                        <Pencil size={12} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => deleteListing(l)} data-testid={`delete-listing-${l.id}`} title="Delete listing">
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === "applications" && (
        <Card className="border-white/10 bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead>Applicant</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Income</TableHead>
                <TableHead>Move-in</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {applications.length === 0 && (
                <TableRow className="border-white/5"><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm" data-testid="applications-empty">
                  No applications yet. Once a listing is published, applications will appear here.
                </TableCell></TableRow>
              )}
              {applications.map((a) => (
                <TableRow key={a.id} className="border-white/5" data-testid={`app-row-${a.id}`}>
                  <TableCell>
                    <div className="font-medium">{a.applicant_name}</div>
                    <div className="text-xs text-muted-foreground">{a.applicant_email}</div>
                  </TableCell>
                  <TableCell>{a.unit}</TableCell>
                  <TableCell className="text-right font-mono">${(a.monthly_income || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{a.desired_move_in}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{(a.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell>{status_pill(a.status)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openApp(a)} data-testid={`review-${a.id}`}>Review</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* New / Edit listing dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="listing-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit listing · ${editing.unit}` : "Publish new listing"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Unit</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="lst-unit" disabled={!!editing} /></div>
            <div className="space-y-2"><Label>Monthly rent</Label>
              <Input type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} data-testid="lst-rent" /></div>
            <div className="space-y-2"><Label>Bedrooms</Label>
              <Input type="number" min="0" max="10" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} data-testid="lst-bed" /></div>
            <div className="space-y-2"><Label>Bathrooms</Label>
              <Input type="number" step="0.5" min="0" max="10" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} data-testid="lst-bath" /></div>
            <div className="space-y-2"><Label>Square feet</Label>
              <Input type="number" value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value })} data-testid="lst-sqft" /></div>
            <div className="space-y-2"><Label>Available date</Label>
              <Input type="date" value={form.available_date} onChange={(e) => setForm({ ...form, available_date: e.target.value })} data-testid="lst-avail" /></div>
            <div className="space-y-2 col-span-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="lst-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LISTING_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2"><Label>Amenities (comma-separated)</Label>
              <Input placeholder="Pool, Gym, In-unit Laundry, Balcony" value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} data-testid="lst-amenities" /></div>
            <div className="space-y-2 col-span-2"><Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="lst-desc" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveListing} data-testid="lst-save">{editing ? "Save changes" : "Publish"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application review modal */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="app-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Application · {detail.applicant_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-muted-foreground">Email:</span> {detail.applicant_email}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {detail.applicant_phone}</div>
                  <div><span className="text-muted-foreground">Unit:</span> {detail.unit}</div>
                  <div><span className="text-muted-foreground">Move-in:</span> {detail.desired_move_in}</div>
                  <div><span className="text-muted-foreground">Employer:</span> {detail.employer || "—"}</div>
                  <div><span className="text-muted-foreground">Monthly income:</span> <strong>${(detail.monthly_income || 0).toLocaleString()}</strong></div>
                  <div><span className="text-muted-foreground">Credit range:</span> {detail.credit_range || "—"}</div>
                  <div><span className="text-muted-foreground">Status:</span> {status_pill(detail.status)}</div>
                </div>
                {detail.pets?.length > 0 && <div><span className="text-muted-foreground">Pets:</span> {detail.pets.map((p, i) => `${p.species || ""} ${p.breed || ""}`).join(", ")}</div>}
                {detail.references && <div><span className="text-muted-foreground">References:</span> {detail.references}</div>}
                {detail.notes && <div><span className="text-muted-foreground">Notes:</span> {detail.notes}</div>}

                {detail.screening_report && (
                  <Card className="p-3 border-white/10 bg-white/5" data-testid="screening-report">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Screening Report ({detail.screening_report.provider})</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Income/Rent ratio: <strong>{detail.screening_report.income_to_rent_ratio}x</strong></div>
                      <div>Credit band: <Badge variant="outline">{detail.screening_report.credit_band}</Badge></div>
                      <div>Risk band: <Badge variant="outline">{detail.screening_report.risk_band}</Badge></div>
                      <div>Recommend: <strong className={detail.screening_report.recommend === "approve" ? "text-emerald-500" : detail.screening_report.recommend === "deny" ? "text-rose-400" : "text-amber-500"}>{detail.screening_report.recommend}</strong></div>
                    </div>
                  </Card>
                )}
              </div>
              <DialogFooter className="pt-4">
                {!detail.screening_report && (
                  <Button variant="outline" onClick={runScreening} data-testid="run-screening">Run screening (mocked)</Button>
                )}
                <Button variant="destructive" onClick={() => decide("deny")} data-testid="deny-app" className="gap-2">
                  <XCircle size={14} /> Deny
                </Button>
                <Button onClick={() => decide("approve")} data-testid="approve-app" className="gap-2">
                  <CheckCircle2 size={14} /> Approve → create resident + lease
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
